import { useState, type CSSProperties, type ReactNode } from 'react';
import { C, EASE_SPRING, glass, ink, surface, text } from '../lib/theme';
import { IconAlert, IconExpand } from './icons';

/** Painel padrão do Hub — a superfície de card do mockup. */
export function Panel({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <div style={{ ...surface, ...style }}>{children}</div>;
}

/**
 * Bloco de seção expansível — unidade de composição dos painéis densos
 * (ver `ConfirmadosPanel`). Diferente de `Panel`, que é só uma superfície:
 * este tem cabeçalho próprio e controla seu aberto/fechado, pra o usuário
 * decidir o que ocupa a altura limitada de um modal em vez de rolar tudo.
 */
export function Block({ title, subtitle, count, accent, defaultOpen = true, right, size = 'md', children }: {
  title: string;
  subtitle?: string;
  count?: string;
  accent?: string;
  defaultOpen?: boolean;
  right?: ReactNode;
  /** `lg` reforça o cabeçalho (ícone/título/badge maiores) pra painéis mais
   * respirados como `ConfirmadosPanel` — default `md` preserva o tamanho
   * original. */
  size?: 'md' | 'lg';
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const tone = accent ?? C.accent;
  const lg = size === 'lg';

  return (
    <section style={{
      borderRadius: lg ? 20 : 18, overflow: 'hidden',
      border: `1px solid ${open ? ink(0.11) : ink(0.07)}`,
      background: open ? ink(0.035) : ink(0.018),
      transition: 'background 0.22s ease, border-color 0.22s ease',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: lg ? 15 : 13, padding: open ? (lg ? '18px 20px 15px' : '15px 17px 13px') : (lg ? '15px 20px' : '13px 17px') }}>
        <button
          type="button" onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          title={open ? 'Recolher' : 'Expandir'}
          style={{
            display: 'flex', alignItems: 'center', gap: lg ? 13 : 11, flex: 1, minWidth: 0,
            border: 'none', background: 'none', padding: 0, cursor: 'pointer',
            font: 'inherit', textAlign: 'left', color: 'inherit',
          }}
        >
          <span style={{
            width: lg ? 26 : 22, height: lg ? 26 : 22, borderRadius: 8, flex: 'none', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            background: open ? `${tone}22` : ink(0.06), color: open ? tone : ink(0.4),
            transition: 'background 0.2s, color 0.2s',
          }}>
            <svg width={lg ? 13 : 11} height={lg ? 13 : 11} viewBox="0 0 24 24" fill="none" style={{
              transform: open ? 'rotate(90deg)' : 'none', transition: `transform 0.24s ${EASE_SPRING}`,
            }}>
              <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>

          <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: lg ? 3 : 2 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <span style={{ fontSize: lg ? 16.5 : 14.5, fontWeight: 780, letterSpacing: '-0.014em', color: C.fg }}>{title}</span>
              {count && (
                <span style={{
                  fontSize: lg ? 11.5 : 11, fontWeight: 800, letterSpacing: '0.04em', padding: lg ? '3px 9px' : '2px 8px',
                  borderRadius: 99, background: `${tone}1c`, border: `1px solid ${tone}38`, color: tone,
                  fontVariantNumeric: 'tabular-nums',
                }}>{count}</span>
              )}
            </span>
            {subtitle && <span style={{ fontSize: lg ? 12.5 : 12, color: text.body }}>{subtitle}</span>}
          </span>
        </button>

        {right && <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 9 }}>{right}</div>}
      </div>

      {open && <div style={{ padding: lg ? '3px 20px 20px' : '2px 17px 17px' }}>{children}</div>}
    </section>
  );
}

/**
 * Número de destaque dentro de um bloco — a variante compacta do KpiCard, pra
 * quando a altura é limitada (modal) mas o número ainda é a informação
 * principal. Fonte deliberadamente grande: é o que o olho procura primeiro.
 */
export function StatTile({ label, value, sub, color, glow, strong, size = 'md' }: {
  label: string;
  value: string;
  sub?: string;
  color?: string;
  glow?: string;
  strong?: boolean;
  /** `lg` é a variante usada em painéis mais respirados (ex: `ConfirmadosPanel`)
   * — mesmo desenho, só com mais presença. Default `md` preserva o tamanho
   * original pra quem já usa o componente (ex: `FalsePositiveCard`). */
  size?: 'md' | 'lg';
}) {
  const lg = size === 'lg';
  return (
    <div style={{
      padding: lg ? '17px 18px' : '14px 15px', borderRadius: lg ? 16 : 14,
      background: strong ? ink(0.06) : ink(0.035),
      border: `1px solid ${strong ? ink(0.12) : ink(0.075)}`,
      minWidth: 0,
    }}>
      <div style={{
        fontSize: lg ? 11 : 10.5, fontWeight: 800, letterSpacing: '0.13em', textTransform: 'uppercase',
        color: text.title, marginBottom: lg ? 11 : 9, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>{label}</div>
      <div style={{
        fontSize: lg ? (strong ? 29 : 24) : (strong ? 25 : 21), fontWeight: 800, letterSpacing: '-0.03em',
        fontVariantNumeric: 'tabular-nums', lineHeight: 1.1,
        color: color ?? C.fg, textShadow: glow ?? 'none',
      }}>{value}</div>
      {sub && <div style={{ fontSize: lg ? 12.5 : 11.5, color: text.body, marginTop: lg ? 6 : 5 }}>{sub}</div>}
    </div>
  );
}

/**
 * Cabeçalho de seção com âncora — alvo do índice lateral (PRD §6). Sem
 * subtítulo de propósito: títulos de página/seção já são autoexplicativos
 * (pedido explícito), então a hierarquia visual vem só do peso/tamanho do
 * `h2` — maior que antes já que não tem mais uma segunda linha disputando
 * espaço logo abaixo.
 */
export function SectionHeader({ id, title, right }: {
  id?: string; title: string; right?: ReactNode;
}) {
  return (
    <div id={id} style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 20, scrollMarginTop: 150 }}>
      <h2 style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.022em', margin: 0, minWidth: 0 }}>{title}</h2>
      {right && <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>{right}</div>}
    </div>
  );
}

/**
 * Tooltip de fórmula (ⓘ) — exclusivo dos KPI Cards (PRD §6: "explica a
 * fórmula em 1 frase"). Substituiu o `title` nativo do navegador por um
 * popover próprio, porque um `title` não dá controle nenhum de estilo/tempo
 * de entrada — aqui a entrada é um scale-grow (estilo macOS: nasce um pouco
 * menor que o tamanho final e "estoura" levemente pra dentro, `EASE_SPRING`),
 * não um tooltip nativo instantâneo.
 */
export function InfoDot({ title }: { title: string }) {
  const [open, setOpen] = useState(false);
  return (
    <span
      style={{ position: 'relative', display: 'flex', flex: 'none' }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <span
        style={{
          width: 16, height: 16, borderRadius: '50%', background: 'rgba(255,144,112,0.16)',
          color: C.accent, fontFamily: 'Georgia,serif', fontStyle: 'italic', fontSize: 10.5,
          fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'help',
        }}
      >i</span>
      {open && (
        <div style={{
          position: 'absolute', bottom: 'calc(100% + 9px)', left: '50%', zIndex: 90,
          width: 220, padding: '10px 13px', borderRadius: 13,
          transform: 'translateX(-50%) scale(1)', transformOrigin: 'bottom center',
          animation: `koInfoPop 0.2s ${EASE_SPRING}`,
          fontSize: 12, fontWeight: 500, lineHeight: 1.4, color: ink(0.88),
          ...glass, pointerEvents: 'none',
        }}>{title}</div>
      )}
    </span>
  );
}

export function Chip({ children, bg, color, style }: {
  children: ReactNode; bg: string; color: string; style?: CSSProperties;
}) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10.5, fontWeight: 800,
      letterSpacing: '0.07em', textTransform: 'uppercase', padding: '3px 9px', borderRadius: 99,
      background: bg, color, whiteSpace: 'nowrap', ...style,
    }}>{children}</span>
  );
}

/** Botão de expandir gráfico — abre o Scale Modal do PRD §4-A nível 2. */
export function ExpandButton({ onClick, size = 28 }: { onClick: (e: React.MouseEvent) => void; size?: number }) {
  return (
    <button
      type="button" onClick={onClick} title="Expandir"
      className="ko-expand"
      style={{
        width: size, height: size, borderRadius: size > 26 ? 9 : 8, border: `1px solid ${ink(0.14)}`,
        background: ink(0.05), color: ink(0.62), cursor: 'pointer', font: 'inherit',
        flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    ><IconExpand size={size > 26 ? 14 : 12} /></button>
  );
}

/** Bloco de skeleton — PRD §9-B exige skeleton, não spinner de tela cheia. */
export function Skeleton({ height = 76, radius = 16, style }: { height?: number; radius?: number; style?: CSSProperties }) {
  return (
    <div style={{
      height, borderRadius: radius, background: ink(0.06),
      animation: 'koSkel 1.25s ease-in-out infinite', ...style,
    }} />
  );
}

/** Banner de erro por seção — PRD §9-B: "não bloqueia o resto da tela". */
export function ErrorBanner({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', borderRadius: 16,
      background: 'rgba(242,85,90,0.08)', border: '1px solid rgba(242,85,90,0.28)',
    }}>
      <span style={{
        width: 30, height: 30, borderRadius: 10, flex: 'none', background: 'rgba(242,85,90,0.16)',
        color: C.churn, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}><IconAlert size={16} /></span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700 }}>Não foi possível carregar</div>
        <div style={{ fontSize: 12.5, color: ink(0.55), marginTop: 2 }}>{message}</div>
      </div>
      {onRetry && (
        <button type="button" onClick={onRetry} style={{
          border: `1px solid ${ink(0.2)}`, background: ink(0.06), color: C.fg, cursor: 'pointer',
          font: 'inherit', fontSize: 12.5, fontWeight: 650, padding: '7px 14px', borderRadius: 10, flex: 'none',
        }}>Tentar de novo</button>
      )}
    </div>
  );
}

/** Estado vazio. `tone="good"` é a fila zerada — objetivo do produto, não erro (PRD §9-B). */
export function EmptyState({ tone, icon, title, text, action }: {
  tone: 'good' | 'neutral'; icon: ReactNode; title: string; text: string; action?: ReactNode;
}) {
  const good = tone === 'good';
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 10,
      padding: '74px 30px', borderRadius: 22,
      background: good ? 'rgba(46,204,113,0.06)' : ink(0.035),
      border: `1px solid ${good ? 'rgba(46,204,113,0.2)' : ink(0.09)}`,
    }}>
      <div style={{
        width: 52, height: 52, borderRadius: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: good ? 'rgba(46,204,113,0.16)' : ink(0.07),
        color: good ? C.green : ink(0.5),
        boxShadow: good ? '0 10px 30px rgba(46,204,113,0.18)' : 'none',
      }}>{icon}</div>
      <div style={{ fontSize: 18, fontWeight: 750, letterSpacing: '-0.015em' }}>{title}</div>
      <div style={{ fontSize: 13.5, color: ink(0.55), maxWidth: 400 }}>{text}</div>
      {action}
    </div>
  );
}

export function PrimaryButton({ children, onClick, disabled, style }: {
  children: ReactNode; onClick?: () => void; disabled?: boolean; style?: CSSProperties;
}) {
  return (
    <button
      type="button" onClick={onClick} disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
        border: 'none', cursor: disabled ? 'default' : 'pointer', font: 'inherit', fontSize: 13,
        fontWeight: 750, padding: '9px 16px', borderRadius: 12,
        background: `linear-gradient(150deg, ${C.accent}, ${C.accentDeep})`, color: '#fff',
        boxShadow: '0 6px 20px rgba(232,85,48,0.32)', opacity: disabled ? 0.55 : 1,
        transition: `transform 0.25s ${EASE_SPRING}`, ...style,
      }}
    >{children}</button>
  );
}

export function GhostButton({ children, onClick, title, style }: {
  children: ReactNode; onClick?: () => void; title?: string; style?: CSSProperties;
}) {
  return (
    <button
      type="button" onClick={onClick} title={title} className="ko-ghost"
      style={{
        border: `1px solid ${ink(0.13)}`, background: ink(0.05), color: ink(0.68), cursor: 'pointer',
        font: 'inherit', fontSize: 11.5, fontWeight: 650, padding: '6px 9px', borderRadius: 10,
        whiteSpace: 'nowrap', ...style,
      }}
    >{children}</button>
  );
}
